/**
 * @file DeliveryTotalsCard.tsx
 * @description Плитка «Итог» блока «Доставка» (phase19.24 шаг 5.2, §3).
 *
 * На телефоне стоит ВЫШЕ формы (§3, 360): менеджер диктует клиенту цифры по
 * телефону и ищет их первым делом, а способ получения выбирает уже потом.
 * На `≥lg` — правая колонка рядом с формой, чтобы сумма менялась на глазах.
 *
 * Считает по тем же правилам, что и письмо клиенту: сумма доставки берётся из
 * формы (`receiptDeliveryAmount`), а не из заявки — иначе итог на экране
 * расходился бы с тем, что уйдёт клиенту после сохранения.
 */

import { formatMoney } from "../../../lib/api-orders";
import {
  RECEIPT_METHOD_META,
  receiptDeliveryAmount,
  type ReceiptFormValue,
} from "../../../lib/receipt-method";
import { SectionLabel } from "../../ui/SectionLabel";

interface Props {
  /** Сумма товаров заказа строкой (из заявки — там она без доставки). */
  itemsTotal: string;
  value: ReceiptFormValue;
}

export function DeliveryTotalsCard({ itemsTotal, value }: Props) {
  const delivery = receiptDeliveryAmount(value);
  const total = (Number(itemsTotal) || 0) + (delivery ?? 0);
  const meta = RECEIPT_METHOD_META[value.method];

  return (
    <section className="flex flex-col gap-2 rounded-xl bg-surface-low px-4 py-3">
      <SectionLabel>Итог</SectionLabel>
      <dl className="flex flex-col gap-1.5 text-sm">
        <Row label="Товары" value={`${formatMoney(itemsTotal)} ₽`} />
        <Row
          label="Доставка"
          value={
            delivery === null
              ? meta.deliveryNote
              : delivery === 0
                ? "0,00 ₽ (бесплатно)"
                : `${formatMoney(String(delivery))} ₽`
          }
        />
        <Row
          label="Итого к оплате"
          value={`${formatMoney(String(total))} ₽`}
          highlight
        />
      </dl>
    </section>
  );
}

function Row({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <dt className="text-on-surface-variant">{label}</dt>
      <dd
        className={
          highlight ? "font-semibold text-primary tabular-nums" : "font-medium tabular-nums"
        }
      >
        {value}
      </dd>
    </div>
  );
}
